import { utils } from "./utils.js"
import { Cursor } from "./cursor.js"
import { Client } from "./client.js"
import { UndoManager } from "./undo-manager.js"
import { WrappedOperation } from "./wrapped-operation.js"

export const EditorClient = (function () {
  "use strict"

  function SelfMeta(cursorBefore, cursorAfter) {
    this.cursorBefore = cursorBefore
    this.cursorAfter = cursorAfter
  }

  SelfMeta.prototype.invert = function () {
    return new SelfMeta(this.cursorAfter, this.cursorBefore)
  }

  SelfMeta.prototype.compose = function (other) {
    return new SelfMeta(this.cursorBefore, other.cursorAfter)
  }

  SelfMeta.prototype.transform = function (operation) {
    return new SelfMeta(
      this.cursorBefore ? this.cursorBefore.transform(operation) : null,
      this.cursorAfter ? this.cursorAfter.transform(operation) : null
    )
  }

  function OtherClient(id, editorAdapter) {
    this.id = id
    this.editorAdapter = editorAdapter
  }

  OtherClient.prototype.setColor = function (color) {
    this.color = color
  }

  OtherClient.prototype.updateCursor = function (cursor) {
    this.removeCursor()
    this.cursor = cursor
    this.mark = this.editorAdapter.setOtherCursor(cursor, this.color, this.id)
  }

  OtherClient.prototype.removeCursor = function () {
    if (this.mark) {
      this.mark.clear()
    }
  }

  function EditorClient(serverAdapter, editorAdapter) {
    Client.call(this)
    this.serverAdapter = serverAdapter
    this.editorAdapter = editorAdapter
    this.undoManager = new UndoManager()

    this.clients = {}

    var self = this

    this.editorAdapter.registerCallbacks({
      change: function (operation, inverse) {
        self.onChange(operation, inverse)
      },
      cursorActivity: function () {
        self.onCursorActivity()
      },
      blur: function () {
        self.onBlur()
      },
      focus: function () {
        self.onFocus()
      },
    })
    this.editorAdapter.registerUndo(function () {
      self.undo()
    })
    this.editorAdapter.registerRedo(function () {
      self.redo()
    })

    this.serverAdapter.registerCallbacks({
      ack: function () {
        self.serverAck()
        if (self.focused && self.state instanceof Client.Synchronized) {
          self.updateCursor()
          self.sendCursor(self.cursor)
        }
        self.emitStatus()
      },
      retry: function () {
        self.serverRetry()
      },
      operation: function (operation) {
        self.applyServer(operation)
      },
      cursor: function (clientId, cursor, color) {
        if (
          self.serverAdapter.userId_ === clientId ||
          !(self.state instanceof Client.Synchronized)
        ) {
          return
        }
        var client = self.getClientObject(clientId)
        if (cursor) {
          if (color) client.setColor(color)
          client.updateCursor(Cursor.fromJSON(cursor))
        } else {
          client.removeCursor()
        }
      },
    })
  }

  inherit(EditorClient, Client)

  EditorClient.prototype.getClientObject = function (clientId) {
    var client = this.clients[clientId]
    if (client) {
      return client
    }
    return (this.clients[clientId] = new OtherClient(
      clientId,
      this.editorAdapter
    ))
  }

  EditorClient.prototype.applyUnredo = function (operation) {
    this.undoManager.add(this.editorAdapter.invertOperation(operation))
    this.editorAdapter.applyOperation(operation.wrapped)
    this.cursor = operation.meta.cursorAfter
    if (this.cursor) this.editorAdapter.setCursor(this.cursor)
    this.applyClient(operation.wrapped)
  }

  EditorClient.prototype.undo = function () {
    var self = this
    if (!this.undoManager.canUndo()) {
      return
    }
    this.undoManager.performUndo(function (o) {
      self.applyUnredo(o)
    })
  }

  EditorClient.prototype.redo = function () {
    var self = this
    if (!this.undoManager.canRedo()) {
      return
    }
    this.undoManager.performRedo(function (o) {
      self.applyUnredo(o)
    })
  }

  EditorClient.prototype.onChange = function (textOperation, inverse) {
    var cursorBefore = this.cursor
    this.updateCursor()

    var compose =
      this.undoManager.undoStack.length > 0 &&
      inverse.shouldBeComposedWithInverted(
        last(this.undoManager.undoStack).wrapped
      )
    var inverseMeta = new SelfMeta(this.cursor, cursorBefore)
    this.undoManager.add(new WrappedOperation(inverse, inverseMeta), compose)
    this.applyClient(textOperation)
  }

  EditorClient.prototype.updateCursor = function () {
    this.cursor = this.editorAdapter.getCursor()
  }

  EditorClient.prototype.onCursorActivity = function () {
    var oldCursor = this.cursor
    this.updateCursor()
    if (!this.focused || (oldCursor && this.cursor.equals(oldCursor))) {
      return
    }
    this.sendCursor(this.cursor)
  }

  EditorClient.prototype.onBlur = function () {
    this.cursor = null
    this.sendCursor(null)
    this.focused = false
  }

  EditorClient.prototype.onFocus = function () {
    this.focused = true
    this.onCursorActivity()
  }

  EditorClient.prototype.sendCursor = function (cursor) {
    if (this.state instanceof Client.AwaitingWithBuffer) {
      return
    }
    this.serverAdapter.sendCursor(cursor)
  }

  EditorClient.prototype.sendOperation = function (operation) {
    this.serverAdapter.sendOperation(operation)
    this.emitStatus()
  }

  EditorClient.prototype.applyOperation = function (operation) {
    this.editorAdapter.applyOperation(operation)
    this.updateCursor()
    this.undoManager.transform(new WrappedOperation(operation, null))
  }

  EditorClient.prototype.emitStatus = function () {
    var self = this
    setTimeout(function () {
      self.trigger("synced", self.state instanceof Client.Synchronized)
    }, 0)
  }

  // Set Const.prototype.__proto__ to Super.prototype
  function inherit(Const, Super) {
    function F() {}
    F.prototype = Super.prototype
    Const.prototype = new F()
    Const.prototype.constructor = Const
  }

  function last(arr) {
    return arr[arr.length - 1]
  }

  return EditorClient
})()

utils.makeEventEmitter(EditorClient, ["synced"])
